import React, { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import "./styles/DemoLeads.css";

const STATUS_OPTIONS = ["all", "new", "contacted", "scheduled", "closed", "spam"];
const SPAM_REASON_LABELS = new Map([
	["honeypot", "Hidden form field filled"],
	["suspicious_text", "Suspicious text"],
	["fast_submission", "Submitted too quickly"],
	["missing_form_metadata", "Missing form metadata"],
	["frequent_submissions", "Frequent submissions"],
]);
const EMAIL_STATUS_LABELS = new Map([
	["suppressed_spam", "Suppressed (spam)"],
	["pending", "Pending"],
	["sent", "Sent"],
	["failed", "Failed"],
]);

const emailStatusLabel = (lead) =>
	EMAIL_STATUS_LABELS.get(lead.notificationEmailStatus) ||
	(lead.notificationEmailSent
		? "Sent"
		: lead.notificationEmailError
			? "Failed"
			: "Not sent");

const formatDate = (value) => {
	if (!value) return "--";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "--";
	return date.toLocaleString();
};

const DemoLeads = () => {
	const [leads, setLeads] = useState([]);
	const [statusFilter, setStatusFilter] = useState("new");
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState(null);
	const [saving, setSaving] = useState(null);
	const [error, setError] = useState("");
	const [drafts, setDrafts] = useState({});
	const requestSequence = useRef(0);
	const loadInFlight = useRef(null);
	const saveInFlight = useRef(false);
	const busy = loading || Boolean(saving);

	const loadLeads = useCallback(async (status, cursor = null, replace = false) => {
		if (saveInFlight.current || (loadInFlight.current !== null && !replace)) return;
		const requestId = ++requestSequence.current;
		loadInFlight.current = requestId;
		setLoading(true);
		setLoadingMore(cursor !== null);
		setError("");
		if (cursor === null) {
			setLeads([]);
			setDrafts({});
			setNextCursor(null);
		}
		try {
			const listLeads = httpsCallable(functions, "listScervDemoLeads");
			const response = await listLeads({ status, ...(cursor !== null ? { cursor } : {}) });
			if (requestId !== requestSequence.current) return;
			const nextLeads = response.data?.leads || [];
			setLeads((current) => {
				const byId = new Map(current.map((lead) => [lead.id, lead]));
				nextLeads.forEach((lead) => {
					if (!byId.has(lead.id)) byId.set(lead.id, lead);
				});
				return [...byId.values()];
			});
			setDrafts((current) => {
				const next = { ...current };
				nextLeads.forEach((lead) => {
					if (!Object.prototype.hasOwnProperty.call(next, lead.id)) {
						next[lead.id] = { status: lead.status || "new", notes: lead.notes || "" };
					}
				});
				return next;
			});
			setNextCursor(response.data?.nextCursor ?? null);
		} catch (err) {
			if (requestId !== requestSequence.current) return;
			console.error("Error loading demo leads:", err);
			setError("Failed to load demo leads.");
		} finally {
			if (requestId === requestSequence.current) {
				loadInFlight.current = null;
				setLoading(false);
				setLoadingMore(false);
			}
		}
	}, []);

	useEffect(() => {
		loadLeads("new");
		return () => {
			requestSequence.current += 1;
			loadInFlight.current = null;
		};
	}, [loadLeads]);

	const changeFilter = (status) => {
		if (saveInFlight.current || status === statusFilter) return;
		setStatusFilter(status);
		loadLeads(status, null, true);
	};

	const updateDraft = (leadId, key, value) => {
		setDrafts((current) => ({
			...current,
			[leadId]: {
				...(current[leadId] || {}),
				[key]: value,
			},
		}));
	};

	const saveLead = async (leadId, restore = false) => {
		if (saveInFlight.current || loadInFlight.current !== null) return;
		saveInFlight.current = true;
		setSaving({ leadId, restore });
		setError("");
		try {
			const updateLead = httpsCallable(functions, "updateScervDemoLead");
			const draft = drafts[leadId] || {};
			const status = restore ? "new" : draft.status || "new";
			const response = await updateLead({
				leadId,
				status,
				notes: draft.notes || "",
			});
			const updatedLead = response.data?.lead;
			if (!updatedLead || updatedLead.id !== leadId || updatedLead.status !== status) {
				throw new Error("Demo lead update was not confirmed.");
			}
			setLeads((current) =>
				current
					.map((lead) => (lead.id === leadId ? updatedLead : lead))
					.filter((lead) => !restore || statusFilter !== "spam" || lead.id !== leadId),
			);
			setDrafts((current) => ({
				...current,
				[leadId]: { status: updatedLead.status, notes: updatedLead.notes ?? draft.notes ?? "" },
			}));
		} catch (err) {
			console.error("Error updating demo lead:", err);
			setError("Failed to update demo lead.");
		} finally {
			saveInFlight.current = false;
			setSaving(null);
		}
	};

	return (
		<div className="demo-leads-container">
			<section className="demo-leads-header">
				<h1>{statusFilter === "spam" ? "Spam queue" : "Demo Leads"}</h1>
				<button type="button" onClick={() => loadLeads(statusFilter)} disabled={busy}>
					{loading && !loadingMore ? "Refreshing..." : "Refresh"}
				</button>
			</section>

			<div className="demo-leads-filters">
				<label htmlFor="statusFilter">Status</label>
				<select
					id="statusFilter"
					value={statusFilter}
					onChange={(event) => changeFilter(event.target.value)}
					disabled={Boolean(saving)}
				>
					{STATUS_OPTIONS.map((status) => (
						<option value={status} key={status}>
							{status === "spam" ? "Spam queue" : status === "all" ? "All" : status}
						</option>
					))}
				</select>
			</div>

			{error && <p className="demo-leads-error" role="alert">{error}</p>}
			{loading && !loadingMore && <p className="demo-leads-empty" role="status">Loading demo leads...</p>}
			{!loading && leads.length === 0 && (
				<p className="demo-leads-empty">{statusFilter === "spam" ? "No spam leads found." : "No demo leads found."}</p>
			)}

			<div className="demo-leads-grid">
				{leads.map((lead) => {
					const draft = drafts[lead.id] || {};
					const isSpam = lead.status === "spam";
					const spamReasons = [...new Set(
						(Array.isArray(lead.spamReasons) ? lead.spamReasons : [])
							.map((reason) => SPAM_REASON_LABELS.get(reason) || "Other spam signal"),
					)];
					return (
						<article className={`demo-lead-card${isSpam ? " demo-lead-card-spam" : ""}`} key={lead.id}>
							<div className="demo-lead-card-header">
								<div>
									<h2>{lead.restaurantName || "Unnamed restaurant"}</h2>
									<p>{lead.name || "Unknown contact"}</p>
								</div>
								<span className={`demo-lead-status ${lead.status || "new"}`}>
									{isSpam ? "Spam queue" : lead.status || "new"}
								</span>
							</div>

							<dl className="demo-lead-details">
								<div>
									<dt>Email</dt>
									<dd>
										<a href={`mailto:${lead.email}`}>{lead.email || "--"}</a>
									</dd>
								</div>
								<div>
									<dt>Phone</dt>
									<dd>
										<a href={`tel:${lead.phone}`}>{lead.phone || "--"}</a>
									</dd>
								</div>
								<div>
									<dt>Submitted</dt>
									<dd>{formatDate(lead.createdAt)}</dd>
								</div>
								<div>
									<dt>Email alert</dt>
									<dd>{emailStatusLabel(lead)}</dd>
								</div>
								{Number.isInteger(lead.submissionCount) && lead.submissionCount > 1 && (
									<div>
										<dt>Submissions</dt>
										<dd>{lead.submissionCount}</dd>
									</div>
								)}
								{lead.lastSubmittedAt && (
									<div>
										<dt>Last submitted</dt>
										<dd>{formatDate(lead.lastSubmittedAt)}</dd>
									</div>
								)}
							</dl>

							{isSpam && (
								<div className="demo-lead-spam-reasons">
									<strong>Spam reasons</strong>
									{spamReasons.length > 0 ? (
										<ul>{spamReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
									) : <p>No reasons recorded.</p>}
								</div>
							)}

							{lead.message && (
								<div className="demo-lead-message">
									<strong>Message</strong>
									<p>{lead.message}</p>
								</div>
							)}

							<div className="demo-lead-controls">
								<label>
									Status
									<select
										disabled={busy}
										value={draft.status || lead.status || "new"}
										onChange={(event) =>
											updateDraft(lead.id, "status", event.target.value)
										}
									>
										{STATUS_OPTIONS.filter((status) => status !== "all").map(
											(status) => (
												<option value={status} key={status}>
													{status}
												</option>
											),
										)}
									</select>
								</label>
								<label>
									Notes
									<textarea
										disabled={busy}
										value={draft.notes || ""}
										onChange={(event) =>
											updateDraft(lead.id, "notes", event.target.value)
										}
										rows={3}
										placeholder="Internal follow-up notes..."
									/>
								</label>
								<div className="demo-lead-actions">
									{isSpam && (
										<button type="button" onClick={() => saveLead(lead.id, true)} disabled={busy}>
											{saving?.leadId === lead.id && saving.restore ? "Restoring..." : "Not spam"}
										</button>
									)}
									<button type="button" onClick={() => saveLead(lead.id)} disabled={busy}>
										{saving?.leadId === lead.id && !saving.restore ? "Saving..." : "Save Lead"}
									</button>
								</div>
							</div>
						</article>
					);
				})}
			</div>
			{nextCursor !== null && (
				<div className="demo-leads-pagination">
					<button type="button" onClick={() => loadLeads(statusFilter, nextCursor)} disabled={busy}>
						{loadingMore ? "Loading more..." : "Load more"}
					</button>
				</div>
			)}
		</div>
	);
};

export default DemoLeads;
